import { Link } from 'react-router-dom';
import type { ContentItem } from '@/types/content';
import { formatViews, getDetailPath } from '@/utils/content';

interface ContentCardProps {
  item: ContentItem;
  layout?: 'vertical' | 'horizontal';
  showSummary?: boolean;
  rank?: number;
}

const typeLabel: Record<string, string> = {
  news: '资讯',
  service: '服务',
  entertainment: '娱乐',
};

export default function ContentCard({
  item,
  layout = 'vertical',
  showSummary = true,
  rank,
}: ContentCardProps) {
  const to = getDetailPath(item);

  if (layout === 'horizontal') {
    return (
      <Link
        to={to}
        className="group flex gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 md:gap-4 md:p-4"
      >
        <div className="relative h-24 w-32 shrink-0 overflow-hidden rounded-md md:h-28 md:w-40">
          <img src={item.image} alt={item.title} title={item.title} className="h-full w-full object-cover object-top" />
          <span className="absolute left-2 top-2 rounded-full bg-secondary-950/70 px-2 py-0.5 text-[10px] font-medium text-background-50">
            {typeLabel[item.type]}
          </span>
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          {rank && <span className="mb-1 text-xs font-bold text-accent-600">NO.{rank}</span>}
          <h3 className="line-clamp-2 font-medium text-foreground-950 transition-colors group-hover:text-primary-600 md:text-[15px]">
            {item.title}
          </h3>
          {showSummary && (
            <p className="mt-1.5 line-clamp-2 hidden text-xs leading-relaxed text-secondary-500 sm:block">
              {item.summary}
            </p>
          )}
          <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 pt-2 text-[11px] text-secondary-400">
            <span className="flex items-center gap-1">
              <i className="ri-price-tag-3-line"></i>
              {item.subCategory}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {item.publishTime.slice(0, 10)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatViews(item.views)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={to}
      className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
    >
      <div className="relative h-44 w-full overflow-hidden md:h-48">
        <img
          src={item.image}
          alt={item.title}
          title={item.title}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 rounded-full bg-secondary-950/70 px-2.5 py-0.5 text-[11px] font-medium text-background-50">
          {item.subCategory}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="line-clamp-2 font-medium text-foreground-950 transition-colors group-hover:text-primary-600 md:text-base">
          {item.title}
        </h3>
        {showSummary && (
          <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-secondary-500 md:text-sm">{item.summary}</p>
        )}
        <div className="mt-auto flex items-center justify-between pt-3 text-[11px] text-secondary-400">
          <span className="flex items-center gap-1">
            <i className="ri-time-line"></i>
            {item.publishTime.slice(0, 10)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {formatViews(item.views)}
          </span>
        </div>
      </div>
    </Link>
  );
}